import { useQuery } from "@tanstack/react-query"

import type { JobPostingStage } from "@/components/applicants/DocumentsList"
import type { Session } from "@/hooks/useSessions"
import { supabase } from "@/lib/supabase"
import type { DriverLicenceRecord } from "@/types/driverLicence"
import { resolveSessionAddress } from "@/types/sessionContentFields"
import { SIGNED_URL_EXPIRY, createSignedDocumentUrl } from "@/utils/storageUtils"

const APPLICATION_SELECT = `
  id,
  application_id,
  contract_salary,
  current_stage,
  current_segment,
  status,
  stage_reason,
  stage_note,
  created_at,
  updated_at,
  primary_person:people!applications_primary_person_id_fkey(person),
  vehicle_booking,
  person:people!applications_person_id_fkey(
    id,
    person,
    first_name,
    surname,
    email,
    person_id
  ),
  applicant:applicants!applications_applicant_id_fkey(*),
  job:job_id(
    *,
    depot:depot_id(*)
  ),
  user_id
` as const

async function fetchApplicationDetails(applicationId: string) {
  const { data: applicationData, error: applicationError } = await supabase
    .from("applications")
    .select(APPLICATION_SELECT)
    .eq("id", applicationId)
    .single()

  if (applicationError) {
    console.error("Error fetching application details:", applicationError)
    throw new Error(applicationError.message)
  }

  if (!applicationData) {
    return null
  }

  const [
    { data: documentsData, error: documentsError },
    { data: sessionBookingsData, error: sessionBookingsError },
    { data: trialBlocksData, error: trialBlocksError },
    { data: assessmentData, error: assessmentError },
    { data: driverLicenceRecordsData, error: driverLicenceRecordsError },
  ] = await Promise.all([
    supabase
      .from("documents")
      .select("*")
      .eq("application_id", applicationId)
      .order("created_at", { ascending: false }),
    supabase
      .from("session_bookings")
      .select("*, session:session_id(*, depot:depot_id(*))")
      .eq("application_id", applicationId),
    supabase.from("trial_blocks").select("*").eq("application_id", applicationId),
    supabase.from("assessments").select("*").eq("application_id", applicationId),
    supabase
      .from("driver_licence_records")
      .select("*")
      .eq("application_id", applicationId)
      .order("created_at", { ascending: false }),
  ])

  if (documentsError) console.error("Error fetching documents:", documentsError)
  if (sessionBookingsError) console.error("Error fetching session bookings:", sessionBookingsError)
  if (trialBlocksError) console.error("Error fetching trial blocks:", trialBlocksError)
  if (assessmentError) console.error("Error fetching assessments:", assessmentError)
  if (driverLicenceRecordsError)
    console.error("Error fetching driver licence records:", driverLicenceRecordsError)

  const { data: userData, error: userError } = await supabase
    .from("users")
    .select("*, primary_person:primary_person_id(*)")
    .eq("id", applicationData.user_id)
    .single()

  if (userError) console.error("Error fetching user:", userError)

  const bookings = sessionBookingsData ?? []

  const orientation_bookings = bookings
    .filter((b) => b.session?.type === "orientation")
    .map((b) => ({
      ...b,
      location: resolveSessionAddress(b.session as Session),
    }))

  const vehicle_bookings = bookings
    .filter((b) => b.session?.type === "vehicle_collection")
    .map((b) => ({
      ...b,
      location: resolveSessionAddress(b.session as Session),
    }))

  const vehicleBooking = applicationData.vehicle_booking as {
    paymentMethodId?: string
    stripeCustomerId?: string
    bookingDate?: string
  } | null

  const documents = await Promise.all(
    (documentsData ?? []).map(async (doc) => {
      if (!doc.file) {
        return doc
      }
      const signedUrl = await createSignedDocumentUrl(doc.file)
      return { ...doc, file: signedUrl }
    }),
  )

  const applicant = applicationData.applicant
  const name = `${applicant?.first_name || ""} ${applicant?.last_name || ""}`.trim()
  const address_display = [applicant?.address_line_1, applicant?.city, applicant?.postcode]
    .filter(Boolean)
    .join(", ")

  return {
    ...applicationData,
    allowed_stages:
      (applicationData.job?.stages as unknown as JobPostingStage[] | undefined) ?? null,
    documents,
    orientation_bookings,
    vehicle_bookings,
    trial_blocks: trialBlocksData ?? [],
    assessments: assessmentData ?? [],
    driver_licence_records: (driverLicenceRecordsData ?? []) as DriverLicenceRecord[],
    user: userData ?? null,
    name,
    address_display,
    headshot: documents.find((doc) => doc.type === "headshot")?.file ?? null,
    bank_proof_status: documentsData?.find((doc) => doc.type === "bank-proof")?.status ?? null,
    stripe_customer_id: vehicleBooking?.stripeCustomerId ?? null,
    stripe_payment_method_id: vehicleBooking?.paymentMethodId ?? null,
    payment_added_date: vehicleBooking?.bookingDate ?? null,
  }
}

export type ApplicationDetails = NonNullable<Awaited<ReturnType<typeof fetchApplicationDetails>>>

export { type DriverLicenceRecord } from "@/types/driverLicence"

export const useApplicationDetails = (applicationId: string | undefined) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["applicationDetails", applicationId],
    queryFn: () => fetchApplicationDetails(applicationId!),
    enabled: !!applicationId,
    staleTime: SIGNED_URL_EXPIRY * 1000,
  })

  return {
    applicationDetails: data ?? null,
    isLoading,
    isError,
    error,
    refetch,
  }
}
